import { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import SiteLayout from "./layouts/SiteLayout";
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import {
  AboutPage,
  CollectionPage,
  ContactPage,
  LegalPage,
  NotFoundPage,
} from "./pages/ContentPages";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import LoginPage from "./pages/LoginPage";
import { useAuth } from "./lib/auth";
import { Route as AdminLayoutRoute } from "./ops-routes/admin";
import { Route as AdminIndexRoute } from "./ops-routes/admin.index";
import { Route as AdminAnalyticsRoute } from "./ops-routes/admin.analytics";
import { Route as AdminCmsRoute } from "./ops-routes/admin.cms";
import { Route as AdminCustomersRoute } from "./ops-routes/admin.customers";
import { Route as AdminFinanceRoute } from "./ops-routes/admin.finance";
import { Route as AdminInventoryRoute } from "./ops-routes/admin.inventory";
import { Route as AdminOrdersRoute } from "./ops-routes/admin.orders";
import { Route as AdminProductsRoute } from "./ops-routes/admin.products";
import { Route as AdminPromotionsRoute } from "./ops-routes/admin.promotions";
import { Route as AdminAfterSalesRoute } from "./ops-routes/admin.after-sales";
import { Route as AdminScheduleRoute } from "./ops-routes/admin.schedule";
import { Route as AdminTechniciansRoute } from "./ops-routes/admin.technicians";
import { Route as AdminTicketsRoute } from "./ops-routes/admin.tickets";
import { Route as AdminTicketDetailRoute } from "./ops-routes/admin.tickets.$id";
import { Route as PortalLayoutRoute } from "./ops-routes/portal";
import { Route as PortalIndexRoute } from "./ops-routes/portal.index";
import { Route as PortalAddressesRoute } from "./ops-routes/portal.addresses";
import { Route as PortalBookingsRoute } from "./ops-routes/portal.bookings";
import { Route as PortalNotificationsRoute } from "./ops-routes/portal.notifications";
import { Route as PortalOrdersRoute } from "./ops-routes/portal.orders";
import { Route as PortalPaymentsRoute } from "./ops-routes/portal.payments";
import { Route as PortalProfileRoute } from "./ops-routes/portal.profile";
import { Route as PortalReturnsRoute } from "./ops-routes/portal.returns";
import { Route as PortalSecurityRoute } from "./ops-routes/portal.security";
import { Route as PortalTicketsRoute } from "./ops-routes/portal.tickets";
import { Route as PortalWarrantyRoute } from "./ops-routes/portal.warranty";
import { Route as PortalWishlistRoute } from "./ops-routes/portal.wishlist";
import TechnicianLayout from "./technician/TechnicianLayout";
import {
  TechnicianHome,
  TechnicianJobs,
  TechnicianJobDetail,
  TechnicianSchedule,
  TechnicianProfile,
} from "./technician/TechnicianPages";

function ModeController() {
  const { pathname } = useLocation();
  useEffect(() => {
    const ops =
      pathname.startsWith("/admin") ||
      pathname.startsWith("/portal") ||
      pathname.startsWith("/account") ||
      pathname.startsWith("/technician");
    document.body.classList.toggle("ops-mode", ops);
    document.documentElement.classList.toggle("ops-mode", ops);
    return () => {
      document.body.classList.remove("ops-mode");
      document.documentElement.classList.remove("ops-mode");
    };
  }, [pathname]);
  return null;
}

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="app-loading">Loading KIZAK…</div>;
  if (!user)
    return (
      <Navigate
        to={role === "admin" ? "/login/admin" : role === "technician" ? "/login/technician" : "/login"}
        replace
        state={{ from: window.location.pathname, role }}
      />
    );
  if (
    role === "admin" &&
    !["admin", "operations", "finance", "support", "super_admin"].includes(
      user.role,
    )
  )
    return (
      <Navigate
        to={user.role === "technician" ? "/technician" : "/portal"}
        replace
      />
    );
  if (role === "technician" && user.role !== "technician")
    return (
      <Navigate
        to={
          ["admin", "operations", "finance", "support", "super_admin"].includes(
            user.role,
          )
            ? "/admin"
            : "/portal"
        }
        replace
      />
    );
  if (role === "customer" && user.role !== "customer")
    return (
      <Navigate
        to={user.role === "technician" ? "/technician" : "/admin"}
        replace
      />
    );
  return children;
}

const OpsRoute = ({ route }) => {
  const Component = route.component;
  return Component ? <Component /> : null;
};

export default function App() {
  return (
    <>
      <ModeController />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/admin" element={<LoginPage />} />
        <Route path="/login/technician" element={<LoginPage />} />
        <Route path="/auth/callback" element={<LoginPage />} />
        <Route
          path="/"
          element={
            <SiteLayout>
              <HomePage />
            </SiteLayout>
          }
        />
        <Route
          path="/shop"
          element={
            <SiteLayout>
              <ShopPage />
            </SiteLayout>
          }
        />
        <Route
          path="/shop/:slug"
          element={
            <SiteLayout>
              <ProductPage />
            </SiteLayout>
          }
        />
        <Route
          path="/collections/:slug"
          element={
            <SiteLayout>
              <CollectionRoute />
            </SiteLayout>
          }
        />
        <Route
          path="/about"
          element={
            <SiteLayout>
              <AboutPage />
            </SiteLayout>
          }
        />
        <Route
          path="/contact"
          element={
            <SiteLayout>
              <ContactPage />
            </SiteLayout>
          }
        />
        <Route
          path="/cart"
          element={
            <SiteLayout>
              <CartPage />
            </SiteLayout>
          }
        />
        <Route
          path="/checkout"
          element={
            <SiteLayout>
              <CheckoutPage />
            </SiteLayout>
          }
        />
        <Route
          path="/legal/privacy-policy"
          element={
            <SiteLayout>
              <LegalPage title="Privacy Policy" />
            </SiteLayout>
          }
        />
        <Route
          path="/legal/cookie-policy"
          element={
            <SiteLayout>
              <LegalPage title="Cookie Policy" />
            </SiteLayout>
          }
        />
        <Route
          path="/legal/terms-and-conditions"
          element={
            <SiteLayout>
              <LegalPage title="Terms and Conditions" />
            </SiteLayout>
          }
        />
        <Route
          path="/legal/delivery-and-return"
          element={
            <SiteLayout>
              <LegalPage title="Delivery and Return" />
            </SiteLayout>
          }
        />

        <Route
          path="/admin"
          element={
            <Protected role="admin">
              <OpsRoute route={AdminLayoutRoute} />
            </Protected>
          }
        >
          <Route index element={<OpsRoute route={AdminIndexRoute} />} />
          <Route
            path="analytics"
            element={<OpsRoute route={AdminAnalyticsRoute} />}
          />
          <Route path="cms" element={<OpsRoute route={AdminCmsRoute} />} />
          <Route
            path="customers"
            element={<OpsRoute route={AdminCustomersRoute} />}
          />
          <Route
            path="finance"
            element={<OpsRoute route={AdminFinanceRoute} />}
          />
          <Route
            path="inventory"
            element={<OpsRoute route={AdminInventoryRoute} />}
          />
          <Route
            path="orders"
            element={<OpsRoute route={AdminOrdersRoute} />}
          />
          <Route
            path="products"
            element={<OpsRoute route={AdminProductsRoute} />}
          />
          <Route
            path="promotions"
            element={<OpsRoute route={AdminPromotionsRoute} />}
          />
          <Route path="after-sales" element={<OpsRoute route={AdminAfterSalesRoute} />} />
          <Route
            path="schedule"
            element={<OpsRoute route={AdminScheduleRoute} />}
          />
          <Route
            path="technicians"
            element={<OpsRoute route={AdminTechniciansRoute} />}
          />
          <Route
            path="tickets"
            element={<OpsRoute route={AdminTicketsRoute} />}
          />
          <Route
            path="tickets/:id"
            element={<OpsRoute route={AdminTicketDetailRoute} />}
          />
        </Route>

        <Route
          path="/portal"
          element={
            <Protected role="customer">
              <OpsRoute route={PortalLayoutRoute} />
            </Protected>
          }
        >
          <Route index element={<OpsRoute route={PortalIndexRoute} />} />
          <Route
            path="addresses"
            element={<OpsRoute route={PortalAddressesRoute} />}
          />
          <Route
            path="bookings"
            element={<OpsRoute route={PortalBookingsRoute} />}
          />
          <Route
            path="notifications"
            element={<OpsRoute route={PortalNotificationsRoute} />}
          />
          <Route
            path="orders"
            element={<OpsRoute route={PortalOrdersRoute} />}
          />
          <Route
            path="payments"
            element={<OpsRoute route={PortalPaymentsRoute} />}
          />
          <Route
            path="profile"
            element={<OpsRoute route={PortalProfileRoute} />}
          />
          <Route
            path="returns"
            element={<OpsRoute route={PortalReturnsRoute} />}
          />
          <Route
            path="security"
            element={<OpsRoute route={PortalSecurityRoute} />}
          />
          <Route
            path="tickets"
            element={<OpsRoute route={PortalTicketsRoute} />}
          />
          <Route
            path="warranty"
            element={<OpsRoute route={PortalWarrantyRoute} />}
          />
          <Route
            path="wishlist"
            element={<OpsRoute route={PortalWishlistRoute} />}
          />
        </Route>
        <Route path="/account" element={<Navigate to="/portal" replace />} />

        <Route
          path="/technician"
          element={
            <Protected role="technician">
              <TechnicianLayout />
            </Protected>
          }
        >
          <Route index element={<TechnicianHome />} />
          <Route path="jobs" element={<TechnicianJobs />} />
          <Route path="jobs/:id" element={<TechnicianJobDetail />} />
          <Route path="schedule" element={<TechnicianSchedule />} />
          <Route path="profile" element={<TechnicianProfile />} />
        </Route>
        <Route
          path="*"
          element={
            <SiteLayout>
              <NotFoundPage />
            </SiteLayout>
          }
        />
      </Routes>
    </>
  );
}
function CollectionRoute() {
  const { slug } = useParams();
  return <CollectionPage slug={slug} />;
}
